import { CitationStyle } from '@/types/project';
import { formatBibliographyEntry, CitationData } from './citations';

export interface BibliographySource {
  id: string;
  title: string;
  type: string;
  source_author: string | null;
  source_publication: string | null;
  source_publication_date: string | null;
  source_url: string | null;
  source_doi: string | null;
  source_isbn: string | null;
  source_page_numbers: string | null;
}

// Convert research item to citation data
function toCitationData(source: BibliographySource): CitationData {
  const year = source.source_publication_date 
    ? new Date(source.source_publication_date).getFullYear().toString()
    : undefined;

  return {
    author: source.source_author || 'Unknown',
    title: source.title,
    publication: source.source_publication || undefined,
    year,
    pageNumbers: source.source_page_numbers || undefined,
    doi: source.source_doi || undefined,
    isbn: source.source_isbn || undefined,
    url: source.source_url || undefined,
    accessDate: new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  };
}

// Sort bibliography entries by author last name
function sortByAuthor(a: BibliographySource, b: BibliographySource): number {
  const authorA = a.source_author || 'Unknown';
  const authorB = b.source_author || 'Unknown';
  const lastNameA = authorA.split(' ').pop() || authorA;
  const lastNameB = authorB.split(' ').pop() || authorB;
  return lastNameA.localeCompare(lastNameB);
}

// Generate bibliography entries
export function generateBibliography(
  sources: BibliographySource[],
  style: CitationStyle
): string[] {
  if (style === 'none' || sources.length === 0) {
    return [];
  }

  // Sort sources alphabetically by author
  const sortedSources = [...sources].sort(sortByAuthor);

  return sortedSources.map(source => {
    const citationData = toCitationData(source);
    return formatBibliographyEntry(citationData, style);
  });
}

// Generate bibliography as formatted text
export function generateBibliographyText(
  sources: BibliographySource[],
  style: CitationStyle
): string {
  const entries = generateBibliography(sources, style);
  
  if (entries.length === 0) {
    return '';
  }

  const header = getBibliographyTitle(style);
  return `${header}\n\n${entries.join('\n\n')}`;
}

// Get the appropriate title for the bibliography section
export function getBibliographyTitle(style: CitationStyle): string {
  switch (style) {
    case 'APA':
      return 'References';
    case 'MLA':
      return 'Works Cited';
    case 'Chicago-Notes':
    case 'Turabian':
      return 'Bibliography';
    case 'Chicago-Author':
      return 'References';
    case 'Harvard':
      return 'Reference List';
    case 'IEEE':
    case 'Vancouver':
    case 'AMA':
      return 'References';
    default:
      return 'Bibliography';
  }
}

// Generate bibliography for PDF export
export function generateBibliographyForPDF(
  sources: BibliographySource[],
  style: CitationStyle
): { title: string; entries: string[] } {
  return {
    title: getBibliographyTitle(style),
    entries: generateBibliography(sources, style),
  };
}

// Generate bibliography for Markdown export
export function generateBibliographyMarkdown(
  sources: BibliographySource[],
  style: CitationStyle
): string {
  const entries = generateBibliography(sources, style);
  
  if (entries.length === 0) {
    return '';
  }

  const title = getBibliographyTitle(style);
  const formattedEntries = entries.map((entry, index) => {
    // For numbered styles, add numbers
    if (style === 'IEEE' || style === 'Vancouver' || style === 'AMA') {
      return `[${index + 1}] ${entry}`;
    }
    return entry;
  });

  return `## ${title}\n\n${formattedEntries.join('\n\n')}`;
}
